/**
 * G.711 µ-law (PCMU) and a-law (PCMA) codec.
 *
 * Reference: ITU-T Recommendation G.711 (1972).
 *
 * The two codecs are closely related — both compand 14-bit signed PCM
 * down to 8 bits via a piecewise-linear approximation of a logarithmic
 * curve. µ-law is standard in North America / Japan; a-law in most of
 * the rest of the world. The MCPTT manager's `SERVER_CONFIG.AudioCodec`
 * value of `1` covers BOTH variants (the bundle's `alawmulaw.js`
 * decoder picks one at runtime based on payload-type) — by default
 * we treat it as µ-law because that's the most common WalkieFleet
 * deployment. If the decoded audio sounds garbled, try `alaw`.
 *
 * Implementation: 256-entry decode tables built once at module load,
 * 16384-entry encode tables built once at module load. Both are O(1)
 * lookups in the hot path.
 */

// ============================================================
// µ-law decode (8-bit -> Int16)
// ============================================================
function buildMulawDecodeTable(): Int16Array {
  const t = new Int16Array(256);
  for (let i = 0; i < 256; i++) {
    let u = ~i & 0xff;
    let sign = (u & 0x80) ? -1 : 1;
    let exponent = (u >> 4) & 0x07;
    let mantissa = u & 0x0f;
    let sample = ((mantissa << 3) + 0x84) << exponent;
    sample -= 0x84;
    t[i] = sign * sample;
  }
  return t;
}
export const MULAW_DECODE = buildMulawDecodeTable();

// ============================================================
// µ-law encode (Int16 -> 8-bit)
// ============================================================
function buildMulawEncodeTable(): Uint8Array {
  // Quantize to 14-bit range; offset is 0x84 (132) per the standard.
  const BIAS = 0x84;
  const CLIP = 32635;
  const t = new Uint8Array(0x10000);
  for (let i = 0; i < 0x10000; i++) {
    // Convert unsigned 16-bit table index to signed.
    let pcm = i > 0x7fff ? i - 0x10000 : i;
    let sign = (pcm < 0) ? 0x80 : 0;
    if (sign) pcm = -pcm;
    if (pcm > CLIP) pcm = CLIP;
    pcm += BIAS;
    let exponent = 7;
    for (let mask = 0x4000; (pcm & mask) === 0 && exponent > 0; exponent--, mask >>= 1) { /* */ }
    const mantissa = (pcm >> (exponent + 3)) & 0x0f;
    t[i] = ~(sign | (exponent << 4) | mantissa) & 0xff;
  }
  return t;
}
export const MULAW_ENCODE = buildMulawEncodeTable();

// ============================================================
// a-law decode (8-bit -> Int16)
// ============================================================
function buildAlawDecodeTable(): Int16Array {
  const t = new Int16Array(256);
  for (let i = 0; i < 256; i++) {
    let a = i ^ 0x55;
    const sign = a & 0x80;
    let exponent = (a & 0x70) >> 4;
    let mantissa = a & 0x0f;
    let sample: number;
    if (exponent === 0) {
      sample = (mantissa << 4) + 8;
    } else {
      sample = ((mantissa << 4) + 0x108) << (exponent - 1);
    }
    t[i] = sign ? -sample : sample;
  }
  return t;
}
export const ALAW_DECODE = buildAlawDecodeTable();

function buildAlawEncodeTable(): Uint8Array {
  const t = new Uint8Array(0x10000);
  for (let i = 0; i < 0x10000; i++) {
    let pcm = i > 0x7fff ? i - 0x10000 : i;
    const sign = pcm < 0 ? 0x00 : 0x80;
    if (pcm < 0) pcm = -pcm - 1;
    if (pcm > 32635) pcm = 32635;
    let seg: number;
    if (pcm < 256) {
      seg = 0; t[i] = (sign | (pcm >> 4)) ^ 0x55;
      continue;
    }
    if (pcm < 512)        seg = 1;
    else if (pcm < 1024)  seg = 2;
    else if (pcm < 2048)  seg = 3;
    else if (pcm < 4096)  seg = 4;
    else if (pcm < 8192)  seg = 5;
    else if (pcm < 16384) seg = 6;
    else                  seg = 7;
    const mantissa = (pcm >> (seg + 3)) & 0x0f;
    t[i] = (sign | (seg << 4) | mantissa) ^ 0x55;
  }
  return t;
}
export const ALAW_ENCODE = buildAlawEncodeTable();

// ============================================================
// Public helpers — the hot-path API
// ============================================================

export type Codec = "mulaw" | "alaw";

/** Decode N bytes of µ-law / a-law payload into Float32 in [-1, 1]. */
export function decodePcm(payload: Uint8Array, codec: Codec): Float32Array {
  const out = new Float32Array(payload.length);
  const table = codec === "mulaw" ? MULAW_DECODE : ALAW_DECODE;
  for (let i = 0; i < payload.length; i++) {
    out[i] = table[payload[i]] / 32768;
  }
  return out;
}

/** Encode Float32 in [-1, 1] to µ-law / a-law bytes. */
export function encodePcm(samples: Float32Array, codec: Codec): Uint8Array {
  const out = new Uint8Array(samples.length);
  const table = codec === "mulaw" ? MULAW_ENCODE : ALAW_ENCODE;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    const pcm = (s * 32767) | 0;
    out[i] = table[pcm & 0xffff];
  }
  return out;
}
